import React, { useMemo, useCallback, useState, useEffect } from "react";
import { MessageResponseType } from "../../types/message";
import { ResourceType } from "../../types/resource";
import { useResourceService } from "../../services/resource/resourceService";
import { useResourceStore } from "../../store/resourceStore";
import { useOfficeSync } from "../../hooks/useOfficeSync";
import { ExchangeResourceTypeEnum } from "../../hooks/useOutlookActions";

// Icons
import moreIcon from "../../../../../assets/icons/more.svg";
import locationIcon from "../../../../../assets/icons/location.svg";
import peopleIcon from "../../../../../assets/icons/people.svg";
import checkIcon from "../../../../../assets/icons/check.svg";
import regularIcon from "../../../../../assets/icons/regular.svg";

type Props = {
  data: MessageResponseType | any;
  onLoadMore?: () => void | Promise<void>;
  onLoadPrevious?: () => void | Promise<void>;
  onViewResourceDetail?: (resource: any) => void;
  loadingResourceId?: string | null;
};

/**
 * Optimized Resource Selection Component
 * - Auto-syncs with Outlook via global useOfficeEvents
 * - Simple, reusable, performant
 */
const ListResourcePrompt = ({ data, onLoadMore, onLoadPrevious, onViewResourceDetail, loadingResourceId }: Props) => {

  // ✅ Core hooks - simplified
  const { toggleResource, isProcessing } = useResourceService();
  const { selectedResource } = useResourceStore();
  const { isOutlookSyncReady, isSyncInProgress, counts } = useOfficeSync();

  // State for dropdown menu
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Loading states for pagination buttons
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);

  // Close dropdown when clicking outside of the currently opened menu
  useEffect(() => {
    if (!openDropdownId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const dropdownEl = document.getElementById(`dropdown-${openDropdownId}`);
      console.log("🔍 ListResourcePrompt: Click outside check - dropdownEl:", dropdownEl, "target:", event.target);
      if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
        console.log("🔍 ListResourcePrompt: Clicking outside dropdown, closing it");
        setOpenDropdownId(null);
      }
    };

    // Use click event instead of mousedown to avoid interference with button clicks
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDropdownId]);

  // ✅ Extract resources data
  const resources = useMemo(() => {
    return data?.resources || [];
  }, [data?.resources]);

  // ✅ Check if resource is selected - strict by email only (avoid name collisions)
  const isResourceSelected = useCallback(
    (resource: any): boolean => {
      if (!resource || !selectedResource.length) return false;

      const email = (resource.email || resource.Email || "").toString().trim().toLowerCase();
      if (!email) return false; // don't infer selection without a reliable identifier

      return selectedResource.some((selected) => (selected.Email || "").toString().trim().toLowerCase() === email);
    },
    [selectedResource]
  );

  // ✅ Handle resource selection - simplified
  const handleResourceToggle = useCallback(
    async (resource: any) => {
      if (isProcessing) return; // Prevent double-clicks

      try {
        const email = resource.email || resource.Email;
        if (!email) {
          console.warn("⚠️ Resource has no email; selection is disabled.");
          return;
        }
        // Normalize resource data
        const normalizedResource: ResourceType = {
          ID: resource.id || resource.ID || undefined,
          Name: resource.name || resource.Name || "Unknown Resource",
          Email: email,
          Location: resource.locationPath || resource.Location || resource.name || "Meeting Room",
          Capacity: Number(resource.capacity || resource.Capacity) || 0,
          IsAvailable: resource.isAvailable !== false,
          ResourceType: resource.type || resource.ResourceType || ExchangeResourceTypeEnum.Equipment,
          RecipientType: resource.recipientType || ExchangeResourceTypeEnum.Equipment,
          Description: resource.description || resource.Description || `Resource: ${resource.name || resource.Name}`,
        };

        const result = await toggleResource(normalizedResource);

        if (!result.success) {
          console.warn("⚠️ Resource toggle failed:", result.message);
        }
      } catch (error) {
        console.error("❌ Error toggling resource:", error);
      }
    },
    [toggleResource, isProcessing]
  );

  const handleClickLoadMore = useCallback(async () => {
    if (!onLoadMore || isLoadingMore) return;
    try {
      setIsLoadingMore(true);
      const maybePromise = onLoadMore();
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        await (maybePromise as Promise<void>);
      }
    } catch (error) {
      console.error("Failed to load more:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [onLoadMore, isLoadingMore]);

  const handleClickLoadPrevious = useCallback(async () => {
    if (!onLoadPrevious || isLoadingPrevious) return;
    try {
      setIsLoadingPrevious(true);
      const maybePromise = onLoadPrevious();
      if (maybePromise && typeof (maybePromise as any).then === "function") {
        await (maybePromise as Promise<void>);
      }
    } catch (error) {
      console.error("Failed to load previous:", error);
    } finally {
      setIsLoadingPrevious(false);
    }
  }, [onLoadPrevious, isLoadingPrevious]);

  // ✅ Loading state
  if (!isOutlookSyncReady) {
    return (
      <div className="w-full flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-sm text-gray-600">Syncing with Outlook...</span>
      </div>
    );
  }

  // ✅ Empty state
  // if (!resources.length) {
  //   return (
  //     <div className="w-full text-center py-8 text-gray-500">
  //       <p>No resources found</p>
  //     </div>
  //   );
  // }

  return (
    <div className="w-full">
      <div className="w-full flex flex-col gap-2 px-0" style={{ overflow: "visible" }}>
        {data.reply && (
          <p className="text-[14px] leading-[20px] font-[400] mb-2 text-darkGray dark:text-softGray">{data.reply}</p>
        )}
        {resources.map((resource: any, index: number) => {
          const resourceId = resource.id || resource.ID || index.toString();
          const isSelected = isResourceSelected(resource);
          const isCurrentlyProcessing = isProcessing;
          const hasEmail = Boolean(resource?.email || resource?.Email);
          // Ensure unique and stable React keys even when IDs are duplicated across pages
          const uniqueKey = `${String(
            resource.id ?? resource.ID ?? resource.email ?? resource.Email ?? resource.name ?? resource.Name ?? "item"
          )}-${index}`;

          return (
            <div
              key={uniqueKey}
              className={`
                relative w-full p-3 rounded-lg bg-white border-[1px] overflow-hidden 
                ${isSelected ? "border-primaryBlue" : "border-[#E5E7EB] dark:border-softGray"}
                ${isCurrentlyProcessing ? "cursor-wait" : hasEmail ? "cursor-pointer" : "cursor-not-allowed"} 
                transition-all duration-200 hover:shadow-sm min-w-0 dark:bg-darkBackground dark:text-softGray
              `}
              onClick={() => {
                if (!hasEmail || isCurrentlyProcessing) return;
                handleResourceToggle(resource);
              }}
            >
              {/* Status indicator dot */}
              <div className="flex items-start gap-3">
                {/* <div
                  className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${resource?.isAvailable !== false ? 'bg-green-500' : 'bg-red-500'
                    }`}
                /> */}

                <div className="flex-1 min-w-0">
                  {/* Meeting title */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 max-w-[80%]">
                      {resource?.isAvailable ? (
                        <>
                          <svg
                            width={20}
                            height={20}
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1Z"
                              fill="white"
                            />
                            <path
                              d="M10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1Z"
                              stroke="white"
                              strokeWidth={2}
                            />
                            <path
                              d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.7069 8.70739L9.70361 12.7074C9.51595 12.8949 9.26147 13.0002 8.99619 13C8.7309 12.9998 8.47655 12.8943 8.28912 12.7065L6.29233 10.7065C5.90212 10.3157 5.90262 9.68254 6.29346 9.29233C6.6843 8.90212 7.31746 8.90262 7.70767 9.29346L8.99765 10.5855L12.2932 7.29261C12.6839 6.90224 13.3171 6.9025 13.7074 7.29318C14.0978 7.68386 14.0975 8.31703 13.7069 8.70739Z"
                              fill="#13A10E"
                            />
                          </svg>
                        </>
                      ) : (
                        <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1Z"
                            fill="white"
                          />
                          <path
                            d="M10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1Z"
                            stroke="white"
                            strokeWidth={2}
                          />
                          <path
                            d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z"
                            fill="#C50F1F"
                          />
                        </svg>
                      )}
                      <h3
                        className="font-semibold text-[13px] leading-[20px] text-gray-900 truncate flex-1 pr-2 gap-2 dark:text-softGray"
                        title={resource?.name || resource?.Name || ""}
                      >
                        {resource.name || resource.Name || ""}
                      </h3>
                    </div>

                    <div className="relative" id={`dropdown-${resourceId}`}>
                      <button
                        className="w-4 h-4 flex justify-center items-center hover:bg-gray-100 rounded dark:bg-softGray dark:hover:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          const resourceId = resource.id || resource.ID || index.toString();
                          setOpenDropdownId(openDropdownId === resourceId ? null : resourceId);
                        }}
                      >
                        <img src={moreIcon} alt="options" className="w-4 h-4 object-contain" />
                      </button>

                      {/* Dropdown menu */}
                      {(() => {
                        const currentResourceId = resource.id || resource.ID || index.toString();
                        const isDropdownOpen = openDropdownId === currentResourceId;
                        return isDropdownOpen;
                      })() && (
                        <div
                          className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {/* <button
                            className="w-full px-3 py-2 text-left text-[12px] text-black hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Handle view calendar action
                              console.log("View calendar for:", resource.name);
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            View calendar
                          </button> */}
                          <button
                            className={`w-full px-3 py-2 text-left text-[12px] text-black hover:bg-gray-50 flex items-center gap-2 rounded-b-lg transition-all ${
                              !onViewResourceDetail
                                ? "opacity-40 cursor-not-allowed"
                                : loadingResourceId ===
                                  (resource?.id ??
                                    resource?.ID ??
                                    resource?.email ??
                                    resource?.Email ??
                                    resource?.name ??
                                    resource?.Name)
                                ? "opacity-60 cursor-wait"
                                : ""
                            }`}
                            onMouseDown={(e) => {}}
                            onMouseUp={(e) => {}}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();

                              if (onViewResourceDetail) {
                                onViewResourceDetail(resource);
                              } else {
                                console.error("❌ ListResourcePrompt: onViewResourceDetail is undefined!");
                              }
                              setOpenDropdownId(null);
                            }}
                            disabled={
                              !onViewResourceDetail ||
                              loadingResourceId ===
                                (resource?.id ??
                                  resource?.ID ??
                                  resource?.email ??
                                  resource?.Email ??
                                  resource?.name ??
                                  resource?.Name)
                            }
                          >
                            {loadingResourceId ===
                            (resource?.id ??
                              resource?.ID ??
                              resource?.email ??
                              resource?.Email ??
                              resource?.name ??
                              resource?.Name) ? (
                              <div className="w-5 h-5 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-[1.5px] border-gray-400 border-t-transparent"></div>
                              </div>
                            ) : (
                              <img src={regularIcon} alt="view detail" className="w-5 h-5" />
                            )}
                            {loadingResourceId ===
                            (resource?.id ??
                              resource?.ID ??
                              resource?.email ??
                              resource?.Email ??
                              resource?.name ??
                              resource?.Name)
                              ? "Loading..."
                              : "View all detail"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Equipment label */}
                  <div>
                    <span className="text-blue-600 text-[12px] font-medium dark:text-blue-400">{resource?.type}</span>
                  </div>

                  {/* Location and capacity info */}
                  <div
                    className="flex items-center gap-2 text-[12px] text-gray-600 min-w-0"
                    title={resource?.locationPath || resource?.Location || ""}
                  >
                    {/* Location - flexible width with truncate */}
                    <div className="flex items-center gap-1 flex-1 min-w-0 max-w-[80%]">
                      <img src={locationIcon} alt="location" className="w-4 h-4 flex-shrink-0" />
                      <span
                        className="truncate dark:text-softGray"
                        title={resource?.locationPath || resource?.Location || ""}
                      >
                        {resource?.locationPath || resource?.Location || ""}
                      </span>
                    </div>

                    {/* <span className="text-gray-400 flex-shrink-0">•</span> */}
                    <div className="flex items-center gap-1 flex-shrink-0 min-w-0">
                      <img src={peopleIcon} alt="capacity" className="w-4 h-4 flex-shrink-0" />
                      <span className="whitespace-nowrap dark:text-softGray">{resource.capacity || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection indicator */}
              {isSelected && (
                <div
                  style={{
                    clipPath: "polygon(100% 0, 0 0, 100% 100%)",
                    top: "-1px",
                    right: "-1px",
                  }}
                  className="absolute w-[22px] h-[22px] z-10 flex items-start justify-end bg-primaryBlue"
                >
                  <img src={checkIcon} alt="selected" className="w-[8px] h-[6px] mt-[4px] mr-[4px]" />
                </div>
              )}
            </div>
          );
        })}
        {/* {data.infoPagination && (
          <div className="w-full text-center py-2 text-gray-500">
            <p>{data.infoPagination}</p>
          </div>
        )} */}

        <div className="w-full text-center py-2 text-gray-500">
          <p>
            Page {data.currentPage} / {data.totalPage}. Total {data.totalResources} resources.
          </p>
        </div>

        {data.hasNext && (
          <div className="w-full flex justify-center items-center py-1">
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-primaryBlue text-[12px] font-medium hover:bg-blue-50 hover:border-blue-200 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              onClick={handleClickLoadMore}
              disabled={isLoadingMore}
              aria-busy={isLoadingMore}
            >
              {isLoadingMore && (
                <span className="inline-block animate-spin rounded-full h-3 w-3 border-[2px] border-current border-t-transparent"></span>
              )}
              <span>{isLoadingMore ? "Loading..." : "Load more"}</span>
              {!isLoadingMore && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="opacity-80"
                >
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>
        )}
        {data.hasPrevious && (
          <div className="w-full flex justify-center items-center py-1">
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 text-[12px] font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
              onClick={handleClickLoadPrevious}
              disabled={isLoadingPrevious}
              aria-busy={isLoadingPrevious}
            >
              {!isLoadingPrevious && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="opacity-80"
                >
                  <path
                    d="M15 6l-6 6 6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {isLoadingPrevious && (
                <span className="inline-block animate-spin rounded-full h-3 w-3 border-[2px] border-current border-t-transparent"></span>
              )}
              <span>{isLoadingPrevious ? "Loading..." : "Load previous"}</span>
            </button>
          </div>
        )}
      </div>

      {/* Debug info in development */}
      {process.env.NODE_ENV === "development" && (
        <div className="mt-4 p-2 bg-gray-100 rounded text-xs text-gray-600 dark:bg-darkBackground dark:text-softGray">
          <div>
            Selected: {selectedResource.length} | Outlook Sync: {isOutlookSyncReady ? "✅" : "❌"} | Processing:{" "}
            {isProcessing ? "⏳" : "✅"} | Sync Status: {isSyncInProgress?.() ? "🔄" : "✅"}
          </div>
          <details className="mt-1">
            <summary className="cursor-pointer text-blue-600">View Selected Resources</summary>
            <div className="mt-1 pl-2 border-l-2 border-blue-200">
              {selectedResource.map((resource, idx) => (
                <div key={idx} className="text-xs">
                  • {resource.Name} ({resource.Email}) - {resource.ResourceType === 0 ? "Equipment" : "Room"}
                </div>
              ))}
              {selectedResource.length === 0 && <div className="text-gray-400 italic">No resources selected</div>}
            </div>
          </details>
          <details className="mt-1">
            <summary className="cursor-pointer text-blue-600">Sync Data Counts</summary>
            <div className="mt-1 pl-2 border-l-2 border-green-200">
              <div>Required Attendees: {counts?.requiredAttendees || 0}</div>
              <div>Optional Attendees: {counts?.optionalAttendees || 0}</div>
              <div>Enhanced Locations: {counts?.enhancedLocation || 0}</div>
              <div>Selected Resources: {counts?.selectedResources || 0}</div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default ListResourcePrompt;
